import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { FeatureSetInterface } from '@modules/user-module/interfaces/feature-set.interface'
import { FeatureInterface } from '@modules/user-module/interfaces/feature.interface'
import { FeatureSetWebTerminal } from '@modules/user-module/constants/feature-set-web-terminal'
import { ResultUserProcessingType } from '@modules/user-module/types/result-user-processing.type'
import { KycAccountStatusEnum } from '@modules/user-module/enums/kyc-account-status.enum'
import { BlockStatusEnum } from '@modules/user-module/enums/block-status.enum'

@Injectable({
	providedIn: 'root',
})
export class ProcessingUserService {
	protected readonly featureSetWebTerminal: string = FeatureSetWebTerminal
	protected readonly kycAccountStatusEnum: typeof KycAccountStatusEnum = KycAccountStatusEnum
	protected readonly blockStatusEnum: typeof BlockStatusEnum = BlockStatusEnum

	public isSuccessProcessedUser: boolean = false

	private readonly _router: Router = inject<Router>(Router)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	get user(): UserDetailInterface | null {
		return this._userHelperService.userDetail$.getValue()
	}

	get allProcessing(): (() => ResultUserProcessingType)[] {
		return [this._processStatus, this._processBrokerId, this._processWebTerminal]
	}

	private _processWebTerminal(): ResultUserProcessingType {
		const featureSets: FeatureSetInterface[] = this.user?.tariff_group?.feature_sets ?? []

		const features: FeatureInterface[] = featureSets.flatMap(
			(featureSet: FeatureSetInterface): FeatureInterface[] => featureSet.features ?? [],
		)

		const hasWebTerminal: boolean = features.some(
			(feature: FeatureInterface): boolean => feature.name === this.featureSetWebTerminal,
		)

		return [
			hasWebTerminal,
			(): void => {
				this._router.navigate(['unavailable-web-terminal'])
			},
		]
	}

	private _processBrokerId(): ResultUserProcessingType {
		const hasBrokerId: boolean = !!this.user?.broker_id

		return [
			hasBrokerId,
			(): void => {
				this._router.navigate(['absent-broker-id'])
			},
		]
	}

	private _processStatus(): ResultUserProcessingType {
		const isSuccessStatus: boolean =
			this.user?.block_status !== this.blockStatusEnum.blocked &&
			this.user?.kyc_account_status !== this.kycAccountStatusEnum.REJECTED &&
			this.user?.kyc_account_status !== this.kycAccountStatusEnum.ACCOUNT_CLOSED &&
			this.user?.kyc_account_status !== this.kycAccountStatusEnum.DISABLED &&
			this.user?.kyc_account_status !== this.kycAccountStatusEnum.SUBMISSION_FAILED

		return [
			isSuccessStatus,
			(): void => {
				this._router.navigate(['blocked-user'])
			},
		]
	}

	public processUser(): void {
		if (!this.user) return

		const resultsProcessing: ResultUserProcessingType[] = this.allProcessing.map(
			(processing: () => ResultUserProcessingType): ResultUserProcessingType => processing.bind(this)(),
		)

		const failedProcessing: ResultUserProcessingType | undefined = resultsProcessing.find(
			([result]: ResultUserProcessingType): boolean => !result,
		)

		this.isSuccessProcessedUser = !failedProcessing

		if (failedProcessing) {
			const callback: Function = failedProcessing[1]
			callback()
			return
		}

		if (!this._router.url.startsWith('/cabinet')) {
			this._router.navigate(['/cabinet'])
		}
	}
}
